"use client";

import CircularRectTextButton from "@/components/CircularButton/styled/CircularRectTextButton";
import ClickPlayLottie from "@/components/ClickPlayLottie";
import { FlexColumn, FlexColumnAlignJustifyCenter, FlexRowAlignCenter } from "@/components/Flex";
import BorderColoredInput from "@/components/styled/input/BorderColoredInput";
import { fetchPostJSON } from "@/fetchFunctions/fetchWrappers";
import { callCheckPassword } from "@/hooks/sharing/graphqlUtils";
import KaiKnowckingDoorAnimation from "@/public/images/lottie/kai-knocking-door-animation.json";
import { iconSizes } from "@/utils/iconProps";
import { spacing } from "@/utils/spacing";
import { themeColors } from "@/utils/themeColors";
import { ItemType } from "@knowt/syncing/graphql/schema";
import { platform } from "@knowt/syncing/platform";
import { TIME } from "@knowt/syncing/utils/dateTimeUtils";
import { ArrowRight } from "lucide-react";
import { useState } from "react";
import { toast } from "react-hot-toast";
import style from "./passwordProtected.module.css";

const PasswordProtectedAndPrivateFileContent = ({
    type,
    itemId,
    fullyPrivate,
}: {
    type?: ItemType;
    itemId?: string;
    fullyPrivate?: boolean;
}) => {
    const [password, setPassword] = useState("");

    const handleSubmitClick = async () => {
        if (!password) {
            toast.error("Please enter a password");
            return;
        }

        const { isAuthenticated } = await callCheckPassword({ password: password, type, itemId });

        if (isAuthenticated) {
            const storage = await platform.storage();
            await storage.setWithExpiry(`${type}-${itemId}-pwd`, password, 3 * TIME.HOUR);
            await fetchPostJSON("/api/cookies/passwords", { password, type, itemId });
            window.location.reload();
        } else {
            toast.error("Incorrect password");
        }
    };

    const renderPasswordInputSection = () => (
        <FlexRowAlignCenter
            className={style.passwordInputContainer}
            style={{ gap: spacing.SM, width: "100%", justifyContent: "space-between" }}>
            <BorderColoredInput
                sx={{
                    maxWidth: "43rem",
                    width: "80%",
                    height: "6rem",
                    borderRadius: "1.5rem",
                }}
                value={password}
                onChange={e => setPassword(e.target.value)}
                onKeyDown={async e => {
                    if (e.key === "Enter") {
                        await handleSubmitClick();
                    }
                }}
                placeholder="Enter password"
            />
            <CircularRectTextButton
                onClick={handleSubmitClick}
                className="bodyBold2"
                sx={{
                    width: "11rem",
                    height: "6rem",
                    backgroundColor: themeColors.primary,
                    color: themeColors.primaryDark,
                    gap: spacing.XXS,
                }}>
                Next
                <ArrowRight size={iconSizes.MD} />
            </CircularRectTextButton>
        </FlexRowAlignCenter>
    );

    return (
        <FlexColumnAlignJustifyCenter
            style={{
                minHeight: fullyPrivate ? "auto" : "95vh",
                gap: spacing.MD,
                padding: "3.6rem",
            }}>
            <ClickPlayLottie
                animationData={KaiKnowckingDoorAnimation}
                style={{
                    width: "21.4rem",
                    height: "21.4rem",
                }}
            />
            <FlexColumn
                style={{
                    gap: spacing.LG,
                }}>
                <FlexColumn
                    style={{
                        gap: spacing.SM,
                        padding: "0 2rem",
                        textAlign: "center",
                        maxWidth: "57rem",
                    }}>
                    <p
                        className="heading4"
                        data-testid="auto-id-d68898cd-5ed7-418c-ae9d-71b68649792a">
                        {fullyPrivate
                            ? "This file does not exist, or is not publicly available."
                            : "Do you have a password to open the set?"}
                    </p>
                    <p
                        className="body1"
                        data-testid="auto-id-77a5c699-4e09-46b0-9150-cc98b18e2481">
                        {fullyPrivate
                            ? "Looks like you've made a wrong turn, or your friend hasn’t made this file public for you to view. Ask them to share it with you!"
                            : "Looks like this note/flashcard set was shared by a Knowt user on our Plus/Ultra plan. Enter the password to unlock it."}
                    </p>
                </FlexColumn>

                {!fullyPrivate && renderPasswordInputSection()}
            </FlexColumn>
        </FlexColumnAlignJustifyCenter>
    );
};

export default PasswordProtectedAndPrivateFileContent;
